import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../icons";
import { PRESETS_TEMA, TEMA_POR_DEFECTO, Tema, aplicarTema, cargarTema, guardarTema, restablecerTema } from "../theme";

type Empresa = { id: number; nombre: string; nit: string; regimenTributario: string | null; logoUrl: string | null; activo: boolean };

export function Empresa() {
  const { tienePermiso, refrescarSesion } = useAuth();
  const puedeEditar = tienePermiso("core.empresas:editar");
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [nombre, setNombre] = useState("");
  const [regimen, setRegimen] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const data = await api.get("/empresas/actual");
      setEmpresa(data);
      setNombre(data.nombre);
      setRegimen(data.regimenTributario || "");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la empresa.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // --- Tema visual (personal, guardado en este navegador — ver src/theme.ts) ---
  const [tema, setTema] = useState<Tema>(() => cargarTema() || TEMA_POR_DEFECTO);

  const cambiarTema = (nuevo: Tema) => {
    setTema(nuevo);
    aplicarTema(nuevo);
    guardarTema(nuevo);
  };

  const restablecer = () => {
    restablecerTema();
    setTema(TEMA_POR_DEFECTO);
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    setMensaje(null);
    try {
      await api.put("/empresas/actual", { nombre, regimenTributario: regimen });
      setMensaje("Datos de la empresa actualizados.");
      await cargar();
      await refrescarSesion();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Shell titulo="Configuración de empresa" breadcrumb="Core / Configuración">
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <form onSubmit={guardar} className="card p-6 max-w-xl space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Razón social</label>
            <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} disabled={!puedeEditar} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">NIT</label>
            <input className="input-field bg-slate-50" value={empresa?.nit || ""} disabled />
            <p className="text-[11px] text-textmuted mt-1">El NIT es un identificador legal y no se edita desde esta pantalla.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Régimen tributario</label>
            <input className="input-field" value={regimen} onChange={(e) => setRegimen(e.target.value)} disabled={!puedeEditar} placeholder="Régimen común, simple, etc." />
          </div>

          {mensaje && <div className="rounded-md bg-accent-light border border-accent/30 px-3 py-2 text-sm text-accent-dark">{mensaje}</div>}
          {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

          {puedeEditar && (
            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando ? "Guardando..." : "Guardar cambios"}
            </button>
          )}
        </form>
      )}

      <div className="card p-6 max-w-xl mt-6">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="h-8 w-8 rounded-lg bg-accent-light text-accent-dark flex items-center justify-center shrink-0">
            <Icon name="sliders" className="h-4 w-4" />
          </div>
          <h3 className="font-display text-sm font-semibold text-slate-800">Tema</h3>
        </div>
        <p className="text-xs text-textmuted mb-4">
          Personaliza los colores de tu sesión — el cambio se ve al instante en toda la aplicación (barra lateral,
          botones, tarjetas). Se guarda solo en este navegador, no afecta a otros usuarios de la empresa.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
          {PRESETS_TEMA.map((p) => {
            const activo = tema.primario.toLowerCase() === p.primario.toLowerCase() && tema.acento.toLowerCase() === p.acento.toLowerCase();
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => cambiarTema({ primario: p.primario, acento: p.acento })}
                className={`rounded-xl border p-2.5 text-left transition-all ${
                  activo ? "border-accent ring-2 ring-accent/25" : "border-borderc hover:border-slate-300"
                }`}
              >
                <div className="flex overflow-hidden rounded-md h-6 mb-2">
                  <span className="flex-1" style={{ background: p.primario }} />
                  <span className="flex-1" style={{ background: p.acento }} />
                </div>
                <div className="text-[11px] font-medium text-slate-700 leading-tight">{p.nombre}</div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Color principal (barra lateral)</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={tema.primario}
                onChange={(e) => cambiarTema({ ...tema, primario: e.target.value })}
                className="h-9 w-11 rounded-md border border-borderc cursor-pointer bg-white p-1"
              />
              <span className="text-xs text-textmuted font-mono uppercase">{tema.primario}</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Color de acento (botones)</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={tema.acento}
                onChange={(e) => cambiarTema({ ...tema, acento: e.target.value })}
                className="h-9 w-11 rounded-md border border-borderc cursor-pointer bg-white p-1"
              />
              <span className="text-xs text-textmuted font-mono uppercase">{tema.acento}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[11px] text-textmuted">Los cambios se aplican al instante — fíjate en la barra lateral.</span>
          <button type="button" onClick={restablecer} className="text-xs font-semibold text-textmuted hover:text-slate-700 transition-colors">
            Restablecer valores predeterminados
          </button>
        </div>
      </div>
    </Shell>
  );
}
